// import { isServerSide } from "#src/util/helpers.ts";
import {
  defineStore as definePiniaStore,
  type StoreGeneric,
  type Pinia,
  type Store,
  type StateTree,
} from "pinia";
import { type Ref, ref, isRef } from "vue";

export const defineStore = <
  Id extends string,
  S extends StateTree = StateTree,
  G = {},
  A = {},
>(
  id: Id,
  state: () => S
): ((pinia: Pinia, hot?: StoreGeneric) => Store<Id, S, G, A>) => {
  return (pinia: Pinia, hot?: StoreGeneric) => {
    if (!pinia) {
      if ("env" in import.meta && import.meta.env.DEV) {
        console.trace("Missing Pinia instance");
      }
      throw new Error("Pinia instance is required");
    }
    return definePiniaStore(id, state)(pinia, hot) as Store<Id, S, G, A>;
  };
};

type Primitive = string | number | boolean | symbol | bigint | null | undefined;

export type ToSetupState<T> = {
  [K in keyof T]: T[K] extends Primitive
    ? Ref<T[K]>
    : T[K] extends Function
      ? T[K]
      : Ref<T[K]>;
};

export interface SetupStore {
  $reset(): void;
}

function isPrimitive(x: unknown): x is Primitive {
  return x === null || (typeof x !== "object" && typeof x !== "function");
}

function isObjectRecord(x: unknown): x is object {
  return x !== null && typeof x === "object";
}

function typedKeys<T extends object>(obj: T) {
  return Object.keys(obj) as (keyof T)[];
}

export function generateInitialState<T extends object>(
  initial: T
): ToSetupState<T> {
  const state = {} as ToSetupState<T>;

  function assignKey<K extends keyof T>(key: K, value: T[K]) {
    let wrapped: Ref<T[K]> | (T[K] extends Function ? T[K] : never) | Ref<T[K]>;

    if (isPrimitive(value)) {
      wrapped = ref(value) as Ref<T[K]>;
    } else if (isObjectRecord(value)) {
      wrapped = ref(value) as Ref<T[K]>;
    } else {
      wrapped = value as any; // functions pass through
    }

    state[key] = wrapped as ToSetupState<T>[K];
  }

  for (const k of typedKeys(initial)) {
    assignKey(k, initial[k]);
  }

  return state;
}

export function resetState<T extends object>(
  currentState: ToSetupState<T>,
  fresh: T
) {
  (Object.keys(fresh) as (keyof T)[]).forEach(k => {
    const target = currentState[k];
    const next = fresh[k];
    if (isRef(target)) {
      target.value = next as any;
    } else {
      Object.assign(target as object, next as object);
    }
  });
}

export function buildRequestPayload<T extends Record<string, string>>(
  config: T,
  data: Record<keyof T, any>
): any {
  const payload: any = {};

  for (const key in config) {
    const path = config[key].split(".");
    let current = payload;

    for (let i = 0; i < path.length; i++) {
      const segment = path[i];
      if (i === path.length - 1) {
        current[segment] = data[key];
      } else {
        current[segment] ??= {};
        current = current[segment];
      }
    }
  }

  return payload;
}
